import { Button, constants as C, DateWrapper, Tooltip } from '@trucktrax/trucktrax-common';
import { DriverDto, VoidFunc } from '@trucktrax/trucktrax-ts-common';
import cx from 'classnames';
import { isEqual } from 'lodash';
import React, { useCallback } from 'react';
import svgpath from 'svgpath';

import { NOT_AVAILABLE, DTO_URL_PREFIXES } from '../../../../constants/appConstants';
import { MAP_TYPES, MapMarkerType, MapTypes } from '../../../../types';
import { getFullName } from '../../../../util/adminUtil';
import { getIdFromUrl, isEmptyString, noop } from '../../../../util/appUtil';
import recordLog, { LogLevel } from '../../../../util/errorUtil';
import getArrow, { PositionMarkerDataInfo } from '../../../../util/markerUtil';
import {
  getStatusNameFromMapView,
  statusAllowsETACalculation,
  statusAllowsPredictiveRoute,
} from '../../../../util/statusesUtil';
import { isTicketOpen } from '../../../../util/ticketUtil';
import icons from '../icons';
import styles from '../TTMap.module.css';
import { createTruckIcon } from './iconUtils';
import MarkerLabel from './MarkerLabel';
import TTMarker from './TTMarker';
import { MarkerType } from '../TTMap';
import LoadingCircle from '../../../shared/LoadingCircle';

const US_DATE_TIME_COMPACT: string = 'MM/dd/yy h:mma';

type EtaDetails = Pick<google.maps.DistanceMatrixResponseElement, 'duration' | 'distance'>;

const TRUCK_TITLE_STYLES = {
  display: 'flex',
  alignItems: 'center',
  flex: '1 1 auto',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  width: 400,
} as const;

const TRUCK_TITLE_TEXT_STYLES = {
  transform: 'translateY(3px)',
};

const TRUCK_TITLE_ICON_STYLES = {
  transform: 'translateY(3px)',
};

export const getEtaAsString = (milliseconds?: number): string => {
  const etaFormat =
    milliseconds && milliseconds > C.DAYS_IN_MILLISECONDS ? US_DATE_TIME_COMPACT : 'h:mma';
  const etaString =
    (milliseconds && DateWrapper.now.addSeconds(milliseconds).toString(etaFormat)) || '';
  return etaString;
};

export const getHoursAndMinutesAsString = (milliseconds: number): string => {
  const hours = Math.floor(milliseconds / (60 * 60 * 1000));
  const minutes = Math.floor((milliseconds % (60 * 60 * 1000)) / (60 * 1000));
  return `${hours} hr ${minutes} min`;
};

const determinePositionMarkerTitle = (
  position: PositionNameSource,
  markerData: MarkerTypeInfo,
): string | undefined => {
  const trackedObjectName = determineTrackedObjectName(position, markerData);
  const markerType = markerData.type ?? MapMarkerType.Truck;
  const separator = markerType === MapMarkerType.Driver ? ':' : '#';
  return `${markerType}${separator} ${trackedObjectName}`;
};

const determineTrackedObjectName = (
  position: PositionNameSource,
  markerData: MarkerTypeNameSource,
): string | undefined => {
  if (markerData.trackedObjectName) {
    return markerData.trackedObjectName;
  }

  if (position.trackedObject?.truckAlias) {
    return position.trackedObject.truckAlias;
  }
  if (position.trackedObject?.url?.includes(DTO_URL_PREFIXES.DRIVERS)) {
    const driver = position.trackedObject as DriverDto;
    return extractDriverName({
      driverDetails: { firstName: driver.firstName ?? '', lastName: driver.lastName ?? '' },
    });
  }
  if (markerData.truckDetails?.truckAlias) {
    return markerData.truckDetails.truckAlias;
  }
  const name = extractDriverName(markerData);
  return name;
};

export const extractDriverName = (markerData: MarkerTypeNameSource) => {
  let driverName = getFullName(markerData?.driverDetails);
  driverName ??= getFullName(markerData?.truckDetails?.driver);
  if (isEmptyString(driverName)) {
    recordLog(LogLevel.Warn, 'Could not extract driver name from marker data!', { markerData });
  }
  return driverName;
};

const PositionMarker = (props: PositionMarkerProps) => {
  const {
    position,
    selectedItemId,
    markerStatusMap = {},
    markerData,
    hideLabel,
    ticketInfoTicketsCallBack = noop,
    driverInfoMessageCallback = noop,
    isRouteHistory,
    isRouteInfo,
    parentRouteId,
    displayPNG,
    mapType,
    mapRef,
    infoPaneZIndex,
    onShowPredictiveRoute,
    isPredictiveRouteShowing = false,
    isFollowing,
    onToggleFollow,
    onClick,
    isMessagingEnabled = true,
    ...rest
  }: PositionMarkerProps = props;

  const handleShowPredictiveRouteClick = useCallback(
    (_e?: React.MouseEvent<HTMLButtonElement>) => {
      try {
        if (onShowPredictiveRoute) {
          recordLog(LogLevel.Debug, '[PredictiveRoute] Button clicked from PositionMarker', {
            markerId: markerData?.markerId,
            positionId: position?.id,
            status: position?.status,
          });
          onShowPredictiveRoute(markerData, position);
        }
      } catch (e) {
        recordLog(LogLevel.Debug, 'Error requesting predictive route', { error: e });
      }
    },
    [onShowPredictiveRoute, markerData, position],
  );

  try {
    const name = determineTrackedObjectName(position as PositionNameSource, markerData);
    if (isEmptyString(name) || !markerData) {
      const details = {
        positionId: position?.id,
        driver: position?.driver?.url,
        truck: position?.trackedObject?.url,
        markerId: markerData?.markerId,
        markerType: markerData?.type,
      };
      recordLog(LogLevel.Debug, 'Can not create PositionMarker', { details });
      return null;
    }

    position.status = correctStatusByProductLine(position);
    const positionId = position?.id?.toString();
    const markerId = markerData?.markerId;
    // Treat the marker as selected when either the position ID or the marker ID matches the selection payload.
    const isSelected = positionId === selectedItemId || markerId === selectedItemId;
    const isRouteTTMarker = isRouteHistory || isRouteInfo || false;
    const { statusColor, statusText } = generateStatusInfo(position, markerStatusMap);
    const { icon, shadowIcon, iconHighlight } = generateTTMarkerIconConfig(
      position,
      isRouteHistory,
      isRouteInfo,
      displayPNG,
      selectedItemId,
      statusColor,
      mapType ?? MAP_TYPES.ROADMAP,
    );

    const isExternal =
      position.trackedObject?.isExternal || (position.driver as DriverDto)?.isExternal;
    const usesCustomMarkerContent = !displayPNG;
    let customContent: HTMLElement | SVGElement | undefined;

    if (usesCustomMarkerContent) {
      customContent = createTruckIcon(icon, isExternal, isSelected);
    }

    const label = generateTTMarkerLabel(name, hideLabel, statusColor, isExternal);

    const infoPanelOffset: google.maps.Size = generatePixelOffset(
      0,
      0,
      other => other?.width === 0 && other.height === 0,
    );

    const markerPosition = createPositionMarkerInfo(position, statusText, statusColor);

    const infoContent = determineTTMarkerInfoContent(
      markerData,
      position,
      markerPosition,
      isRouteTTMarker,
      ticketInfoTicketsCallBack,
      driverInfoMessageCallback,
      isMessagingEnabled,
      handleShowPredictiveRouteClick,
      isPredictiveRouteShowing,
      isSelected,
      isFollowing,
      onToggleFollow,
    );
    const infoPaneTitle = generateInfoPaneTitle(position as PositionNameSource, markerData);
    const hasOpenTicket = isTicketOpen(markerData?.ticket);

    const marker = (
      <TTMarker
        key={`${markerData.type}_tt_marker_${markerData.markerId}`}
        content={customContent}
        label={label}
        position={markerPosition}
        isExternal={isExternal}
        infoPaneTitle={infoPaneTitle}
        infoPaneContent={infoContent}
        infoPaneOffsetY={25}
        icon={customContent ? undefined : icon}
        iconHighlight={customContent ? undefined : iconHighlight}
        shadowIcon={customContent ? undefined : shadowIcon}
        pixelOffset={infoPanelOffset}
        isSelected={isSelected}
        isRouteHover={isRouteHistory}
        isRouteHistory={isRouteHistory}
        truckHasTicket={hasOpenTicket}
        mapRef={mapRef}
        infoPaneZIndex={infoPaneZIndex}
        onClick={onClick}
        {...rest}
      />
    );
    return marker;
  } catch (e) {
    let message = e;
    if (e instanceof Error) {
      message = e.message;
    }
    recordLog(LogLevel.Debug, `Encountered an error creating position marker element: ${message}`);
    return null;
  }
};

const propsAreEqual = (prevProps: PositionMarkerProps, nextProps: PositionMarkerProps) => {
  const avoidRerender = isEqual(prevProps, nextProps);
  return avoidRerender;
};

export default React.memo(PositionMarker, propsAreEqual);

const correctStatusByProductLine = (position: PositionMarkerDataInfo): string => {
  let { status } = position;
  if (!status) {
    recordLog(LogLevel.Warn, 'Position is missing a status setting it to unknown', {
      positionId: position?.id,
      driver: position.driver?.url,
      trackedObject: position.trackedObject,
    });
    return C.STATUS_IDS.UNKNOWN;
  }

  if (position.productLine !== C.READY_MIX_VALUE && status === C.STATUS_IDS.LOADING) {
    status = C.STATUS_IDS.TICKETED;
  }
  return status;
};

const generatePixelOffset = (
  width: number,
  height: number,
  equals: (other: google.maps.Size) => boolean,
): google.maps.Size => ({
  width,
  height,
  equals,
});

const generateStatusInfo = (
  position: PositionMarkerDataInfo,
  markerStatusMap: any,
): { statusText: string; statusColor: string } => {
  const { status } = position;
  return {
    statusText: markerStatusMap[status!] || markerStatusMap[C.STATUS_IDS.UNKNOWN],
    statusColor: (C.STATUS_COLORS as any)[status!] || C.STATUS_COLORS.Unknown,
  };
};

interface TTMarkerIconConfig {
  icon: any;
  shadowIcon: any;
  iconHighlight: any;
}

const generateTTMarkerIconConfig = (
  position: PositionMarkerDataInfo,
  isRouteInfo: boolean | undefined,
  isRouteHistory: boolean | undefined,
  displayPNG: boolean | undefined,
  selectedItemId: string | undefined,
  statusColor: string | undefined,
  mapType: MapTypes,
): TTMarkerIconConfig => {
  const { status, id } = position;
  const bearing = position.bearing || 0; // just in case bearing is somehow null or undefined

  // arrow icon is 22x22 and (11.5, 11.5) is the center that accounts for the shadow
  const rotatedArrowPath = svgpath(icons.arrowPath)
    .rotate(bearing - 90, 11.5, 11.5)
    .toString();

  let stationary = !(
    (position.status === C.STATUS_IDS.TO_JOB || status === C.STATUS_IDS.RETURNING) &&
    !position.historical
  );

  if (isRouteInfo || isRouteHistory) {
    // ordinarily, bearing will be 0 if and only if speed is also 0
    // however, we've observed at least one instance where a speed was recorded but bearing was 0.
    // It should also be noted that technically a bearing of 0 would mean the user was travelling due north
    // However, in our actual system, north-bound bearings will have near 0 values (e.g. 0.10000000149011612)
    // and missing bearing data will be recorded as 0
    stationary = position.speed === 0 || position.bearing === 0;
  }

  const path = stationary ? icons.circlePath : rotatedArrowPath;

  const strokeColor = mapType === MAP_TYPES.ROADMAP ? 'black' : '#a4d9e9';
  let icon: any = {
    path,
    fillColor: statusColor,
    fillOpacity: 1,
    strokeWeight: 1,
    strokeColor,
    strokeOpacity: 0.5,
    // anchor point of image relative to geolocation
    anchor: createMapPoint(C.GM_ANCHOR / 2, C.GM_ANCHOR / 2),
  };

  if (displayPNG) {
    icon = {
      url: getArrow(position.bearing, status, stationary),
      scaledSize: createMapSize(30, 30),
      // anchor point of image relative to geolocation
      anchor: createMapPoint(15, 15),
    };
  }

  let shadowIcon;
  const iconAnchor = C.GM_ANCHOR / 2;
  const isExternal =
    position.trackedObject?.isExternal || (position.driver as DriverDto)?.isExternal;
  if (!displayPNG && isExternal) {
    const shadowColor = mapType === MAP_TYPES.ROADMAP ? 'black' : '#E2F8FF';
    shadowIcon = {
      ...icon,
      fillColor: shadowColor,
      strokeColor,
      scale: 1.25,
      fillOpacity: isExternal ? 2.0 : 0.4,
      strokeWeight: isExternal ? 4 : 2,
      strokeOpacity: isExternal ? 1.0 : 0.1,
      anchor: createMapPoint(iconAnchor + 0.5, iconAnchor + 0.4),
    };
  }
  const showIconHighlight = selectedItemId === id.toString() && isRouteInfo && !displayPNG;
  const iconHighlight = showIconHighlight
    ? {
        ...icon,
        scale: 1,
        strokeWeight: 5,
        strokeColor: '#3a8eec',
        strokeOpacity: 0.9,
        anchor: createMapPoint(iconAnchor, iconAnchor),
      }
    : undefined;

  return {
    icon,
    shadowIcon,
    iconHighlight,
  };
};

const createMapPoint = (x: number, y: number): google.maps.Point =>
  new window.google.maps.Point(x, y);

const createMapSize = (width: number, height: number) => new window.google.maps.Size(width, height);

const generateTTMarkerLabel = (
  name: string | undefined,
  hideLabel: boolean | undefined,
  statusColor: string,
  isExternal: boolean,
) => {
  if (name && !hideLabel) {
    return <MarkerLabel color={statusColor} name={name} />;
  }
  return undefined;
};

const generateInfoPaneTitle = (position: PositionNameSource, markerData: MarkerTypeInfo) => {
  const { status } = position;
  const title = determinePositionMarkerTitle(position, markerData);
  const tooltipText = `Status: ${getStatusNameFromMapView(position)}`;
  const statusColor = (C.STATUS_COLORS as any)[status!] || C.STATUS_COLORS.Unknown;
  const iconStyles = { color: statusColor, ...TRUCK_TITLE_ICON_STYLES };
  return (
    <div data-test="vehicle-number" title={title} style={TRUCK_TITLE_STYLES}>
      {tooltipText && (
        <Tooltip text={tooltipText}>
          {statusColor && (
            <i
              className={cx(
                styles.dotIcon,
                'icon-check-circle-blank',
                'margin-right-10',
                'clickable',
              )}
              style={iconStyles}
            />
          )}
        </Tooltip>
      )}
      <span className={cx(styles.titleText, 'nowrap-ellipsis')} style={TRUCK_TITLE_TEXT_STYLES}>
        {title}
      </span>
    </div>
  );
};

interface PositionMarkerInfo {
  lat?: number;
  lng?: number;
  bearing: number;
  statusText: string;
  statusColor: string;
}

const createPositionMarkerInfo = (
  position: PositionMarkerDataInfo,
  statusText: string,
  statusColor: string,
): PositionMarkerInfo => ({
  lat: position.location?.latitude,
  lng: position.location?.longitude,
  bearing: position.bearing,
  statusText,
  statusColor,
});

/**
 * Generates the info panel content displayed when a truck/driver marker is selected.
 * @param markerData - Data about the truck/driver marker
 * @param position - Current position details
 * @param driverInfoMessageCallback - Callback for sending messages to drivers
 * @param ticketInfoTicketsCallBack - Callback for viewing ticket details
 */
const generateTruckInfoContent = (
  markerData: MarkerTypeInfo,
  position: PositionMarkerDataInfo,
  driverInfoMessageCallback: Function,
  ticketInfoTicketsCallBack: Function,
  isMessagingEnabled: boolean,
  onShowPredictiveRoute?: (e?: React.MouseEvent<HTMLButtonElement>) => void,
  isPredictiveRouteShowing?: boolean,
  isSelected?: boolean,
  isFollowing?: boolean,
  onToggleFollow?: (markerId: string, center?: { lat: number; lng: number }) => void,
) => {
  const lastUpdate = new DateWrapper(
    markerData.truckDetails?.timestamp || position.updatedTimestamp,
  ).toString(US_DATE_TIME_COMPACT);
  const driverName = extractDriverName(markerData);
  const mailObjectToSend = { ...markerData.driverDetails, fullName: driverName };
  const isExternal = markerData.driverDetails?.isExternal || position.trackedObject?.isExternal;
  const speed = Math.max(0, markerData.truckDetails?.speed ?? position.speed ?? 0);

  const canShowSendMessageButton = isMessagingEnabled && !isExternal;
  const sendMessageButton = canShowSendMessageButton && (
    <Button
      onClick={() => {
        driverInfoMessageCallback(mailObjectToSend);
      }}
      buttonClassName={cx('tt-btn-basic', styles.truckDetailsBtn)}
      iconClassName="icon-mail"
      dataTest="infopane-new-message-button"
      name={C.SEND_MESSAGE}
    />
  );

  const hasOpenTicket = isTicketOpen(markerData?.ticket);
  const canShowViewTicketButton = hasOpenTicket && !!position.trackedObject;
  const viewTicketButton = canShowViewTicketButton && (
    <Button
      onClick={() => {
        ticketInfoTicketsCallBack(markerData);
      }}
      buttonClassName={cx('tt-btn-basic', styles.truckDetailsBtn)}
      iconClassName="icon-tickets"
      dataTest="infopane-ticket-display-button"
      name="View Ticket"
    />
  );

  const ticketStatus = correctStatusByProductLine(position);
  const tooltipText = isPredictiveRouteShowing
    ? 'Hides the predicted route from the map.'
    : 'Shows predicted route to job site. Route may vary due to traffic conditions. Note: Google Maps API calls incur costs.';
  const buttonName = isPredictiveRouteShowing ? 'Hide Predictive Route' : 'Show Predictive Route';
  const showPredictiveRouteButton =
    onShowPredictiveRoute &&
    hasOpenTicket &&
    isSelected &&
    statusAllowsPredictiveRoute(ticketStatus);
  const predictiveRouteButton = showPredictiveRouteButton && (
    <Tooltip placement="right" text={tooltipText}>
      <span>
        <Button
          onClick={(event: React.MouseEvent<HTMLButtonElement>) => onShowPredictiveRoute!(event)}
          buttonClassName={cx('tt-btn-basic', styles.truckDetailsBtn)}
          iconClassName="icon-directions"
          dataTest="infopane-predictive-route-button"
          name={buttonName}
        />
      </span>
    </Tooltip>
  );

  const isTruckOrDriverMarker =
    markerData?.type === MapMarkerType.Driver || markerData?.type === MapMarkerType.Truck;
  const followMarkerId = isTruckOrDriverMarker ? markerData?.markerId : undefined;
  const followLat = position?.location?.lat ?? position?.location?.latitude;
  const followLng = position?.location?.lng ?? position?.location?.longitude;
  const followCenter =
    followLat != null && followLng != null ? { lat: followLat, lng: followLng } : undefined;
  const canFollow = !!onToggleFollow && !!followMarkerId;
  const followButton = canFollow && (
    <Button
      onClick={() => onToggleFollow!(followMarkerId!, followCenter)}
      buttonClassName={cx('tt-btn-basic', styles.truckDetailsBtn)}
      iconClassName={isFollowing ? 'icon-visibility-off' : 'icon-visibility'}
      dataTest="infopane-follow-button"
      name={isFollowing ? 'Unfollow' : 'Follow'}
    />
  );

  const orderNumber = markerData?.ticket?.order?.orderNumber;
  const orderLinkText = orderNumber ? `Order #${orderNumber}` : '';
  const etaDisplayElement = generateEtaDetailsContent(markerData, ticketStatus);
  const orderId = markerData?.ticket?.order ? getIdFromUrl(markerData?.ticket?.order?.url!) : '';
  const orderUrl = `/geotrax/orders/all/${orderId}`;
  return (
    <div>
      <div className={styles.details}>
        {etaDisplayElement}
        <div className={styles.metrics}>
          <div className={styles.flexItem} data-test="infopane-driver-name">
            <b className="margin-right-5 txt-bold">Driver:</b>
            <span className="nowrap-ellipsis" title={driverName}>
              {driverName}
            </span>
          </div>
          <div className={styles.flexItem} data-test="infopane-device-speed">
            <b className="margin-right-5 txt-bold">{C.DEVICE_SPEED}:</b>
            <span>{speed} mph</span>
          </div>
          <div className={styles.flexItem} data-test="infopane-time-stamp">
            <b className="margin-right-5 txt-bold">{C.DATE_TIME}:</b>
            <span className={cx(styles.lastUpdateTime)}>{lastUpdate}</span>
          </div>
          {orderLinkText && (
            <div>
              <a
                className={cx('margin-right-5', 'txt-bold', 'clickable')}
                href={orderUrl}
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  window.location.href = orderUrl;
                }}
              >
                {orderLinkText}
              </a>
            </div>
          )}
        </div>
      </div>
      <div className={styles.truckFooter}>
        {sendMessageButton}
        {viewTicketButton}
        {predictiveRouteButton}
        {followButton}
      </div>
    </div>
  );
};

const generateEtaDetailsContent = (
  markerData: MarkerTypeInfo,
  ticketStatus: string,
): JSX.Element => {
  const isReturning = ticketStatus === C.STATUS_IDS.RETURNING;
  // Validate destination exists before showing ETA:
  // - Returning trucks: require plantLocation (where truck is returning to)
  // - Forward trips: require geoZone coordinates OR address string (job site location)
  const hasDestination = isReturning
    ? !!markerData?.positionDetails?.plantLocation
    : !!(markerData?.ticket?.geoZone || markerData?.ticket?.address);

  const hasTicket = markerData.positionDetails?.ticket != null;
  const shouldShowETA = hasTicket && statusAllowsETACalculation(ticketStatus);
  const etaDetails = extractEtaDetails(markerData);
  const showEtaDetails = shouldShowETA && hasDestination && etaDetails?.distance;
  const showEtaLoader = shouldShowETA && !showEtaDetails;
  const etaAsDate = convertEtaToDateString(etaDetails);
  const etaAsHoursAndMinutes = convertEtaToHoursAndMinutes(etaDetails);

  if (!shouldShowETA) {
    return <></>;
  }

  return (
    <div className={styles.eta}>
      {showEtaLoader && <LoadingCircle size={50} />}
      {showEtaDetails && (
        <>
          <strong className={cx(styles.flexItem, 'txt-bold')}>
            {isReturning ? 'Return ETA:' : 'ETA:'}
          </strong>
          <div className={styles.flexItem}>{etaAsDate}</div>
          <div data-test="infopane-eta-time" className={styles.flexItem}>
            <span>{etaAsHoursAndMinutes}</span>
          </div>
        </>
      )}
    </div>
  );
};

const generateTruckRouteInfoContent = (
  markerData: any,
  isRouteHistory: boolean,
  markerPosition: { lat: number; lng: number; updatedTimestamp: string },
) => {
  const nbsp = '\xa0'; // non-breaking space
  const timestampFormat = `MM/dd/yy'${nbsp}${nbsp}'h:mma`;
  const lastUpdate = new DateWrapper(
    markerData.truckDetails?.timestamp || markerPosition.updatedTimestamp,
  ).toString(timestampFormat);
  const driverName = extractDriverName(markerData);
  const speed = Math.max(0, markerData.truckDetails?.speed ?? 0);
  const orderNumber = markerData?.order?.orderNumber;
  const orderNumberText = orderNumber || '';
  const ticketNumber = markerData.ticket?.ticketNumber;
  const ticketNumberText = ticketNumber || '';

  return (
    <div>
      <div className={styles.details}>
        <div className={styles.metrics}>
          {isRouteHistory && (
            <div className={styles.popupItem} data-test="infopane-order">
              <div className={styles.leftColumn}>{'Order '}</div>
              <div className="right-column">{orderNumberText}</div>
            </div>
          )}
          <div className={styles.popupItem} data-test="infopane-ticket">
            <div className={styles.leftColumn}>Ticket</div>
            <div className="right-column">{ticketNumberText}</div>
          </div>
          <div className={styles.popupItem} data-test="infopane-driver-name">
            <div className={styles.leftColumn}>Driver</div>
            <div className="nowrap-ellipsis, right-column" title={driverName}>
              {driverName}
            </div>
          </div>
          <div className={styles.popupItem} data-test="infopane-device-speed">
            <div className={styles.leftColumn}>Speed</div>
            <div className="right-column">{speed} mph</div>
          </div>
          {isRouteHistory && (
            <div className={styles.popupItem} data-test="infopane-location">
              <div className={styles.leftColumn}>Location</div>
              <div className="right-column">
                {`${markerPosition.lat!.toFixed(5)}`}
                &nbsp;&nbsp;
                {`${markerPosition.lng!.toFixed(5)}`}
              </div>
            </div>
          )}
          <div className={styles.popupItem} data-test="infopane-time-stamp">
            <div className={styles.leftColumn}>Time</div>
            <div className="right-column">{lastUpdate}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export type PositionNameSource = Partial<Pick<PositionMarkerDataInfo, 'trackedObject' | 'status'>>;
export type MarkerTypeInfo = MarkerType & { trackedObjectName?: string };
export type MarkerTypeNameSource = Pick<
  MarkerTypeInfo,
  'driverDetails' | 'truckDetails' | 'trackedObjectName'
>;

const determineTTMarkerInfoContent = (
  markerData: MarkerTypeInfo,
  position: PositionMarkerDataInfo,
  markerPosition: any,
  isRouteTTMarker: boolean,
  ticketInfoTicketsCallBack: Function,
  driverInfoMessageCallback: Function,
  isMessagingEnabled: boolean,
  onShowPredictiveRoute?: (e?: React.MouseEvent<HTMLButtonElement>) => void,
  isPredictiveRouteShowing?: boolean,
  isSelected?: boolean,
  isFollowing?: boolean,
  onToggleFollow?: (markerId: string, center?: { lat: number; lng: number }) => void,
) => {
  if (isRouteTTMarker) {
    return generateTruckRouteInfoContent(markerData, isRouteTTMarker, markerPosition);
  }

  return generateTruckInfoContent(
    markerData,
    position,
    driverInfoMessageCallback,
    ticketInfoTicketsCallBack,
    isMessagingEnabled,
    onShowPredictiveRoute,
    isPredictiveRouteShowing,
    isSelected,
    isFollowing,
    onToggleFollow,
  );
};

export interface PositionMarkerProps {
  position: PositionMarkerDataInfo;
  selectedItemId?: string;
  markerStatusMap?: any;
  markerData?: any;
  ticketInfoTicketsCallBack?: (obj: any) => void;
  driverInfoMessageCallback?: (obj: any) => void;
  onClick?: VoidFunc;
  onCloseClick?: VoidFunc;
  zIndex?: number;
  mapRef?: any;
  isSelected?: boolean;
  onLogoutDriver?: VoidFunc;
  canHover?: boolean;
  hideLabel?: boolean;
  isRouteHistory?: boolean;
  isRouteInfo?: boolean;
  parentRouteId?: number | null;
  displayPNG?: boolean;
  mapType?: MapTypes;
  infoPaneZIndex?: number;
  onShowPredictiveRoute?: (markerData: any, position: any) => void;
  isPredictiveRouteShowing?: boolean;
  isFollowing?: boolean;
  onToggleFollow?: (markerId: string, center?: { lat: number; lng: number }) => void;
  isMessagingEnabled?: boolean;
}

function extractEtaDetails(markerData: any): EtaDetails | undefined {
  let details: EtaDetails | undefined;
  if (markerData?.truckDetails?.distance) {
    details = {
      distance: markerData.truckDetails.distance,
      duration: markerData.truckDetails.duration,
    };
    return details;
  }
  if (markerData?.positionDetails?.distance) {
    details = {
      distance: markerData.positionDetails.distance,
      duration: markerData.positionDetails.duration,
    };
    return details;
  }

  return details;
}

function convertEtaToDateString(eta: EtaDetails | undefined): string {
  return eta?.duration ? getEtaAsString(eta.duration.value) : NOT_AVAILABLE;
}

function convertEtaToHoursAndMinutes(eta: EtaDetails | undefined): string {
  if (eta?.duration?.value && eta?.duration?.value > C.DAYS_IN_MILLISECONDS) {
    return getHoursAndMinutesAsString(eta.duration.value);
  }
  return `${eta?.duration?.text ?? NOT_AVAILABLE}.`;
}
